/**
 * Where the country selector goes (web plan MW7): the same kind of page in the
 * other country, instead of that country's home.
 *
 *  - home, brands, promotions, shops, nearby, search (its `?query=` kept),
 *    category hubs → the same path under the other locale's prefix;
 *  - a brand, shop, store or promotion (`/{type}/{slug or id}`, prefixed or
 *    not), and a brand × city hub (its brand) → the same entity in the other
 *    country when `resolveEntityPath` says it shows there (200, or a 301 to
 *    its path), else that country's home;
 *  - the sale calendar → the other country's, only while it has sale events
 *    (`hasSaleEvents`: the backend's `locales` flags, MW8; Pakistan only while
 *    they are unknown), else its home; an event hub → the same event there when
 *    the other country runs it, else that country's calendar (its home when it
 *    has no events);
 *  - a city hub → the other country's home (a city belongs to one country);
 *  - an account or sign-in page (`/favorites`, `/login`, …) → no navigation:
 *    the page stays and the new country is remembered (@/lib/rememberedLocale);
 *  - any other global page (trust pages, legal pages) → the other
 *    country's home.
 *
 * Pure: the entity lookup is injected, so the plan is testable without Apollo.
 */
import { hasSaleEvents, type SaleCountryFlags } from './saleCountries';
import { DEFAULT_LOCALE, type Locale } from './locales';
import { catalogPath, isAccountPath, localePath, splitLocalePath, type EntityType } from './urls';

/** What `resolveEntityPath` answers (the fields the switch reads). */
export interface EntityResolution {
  status: number;
  canonicalPath?: string | null;
}

/** The lookup of an entity key in a locale; null when it could not be asked. */
export type ResolveEntity = (type: EntityType, key: string, locale: Locale) => Promise<EntityResolution | null>;

/** The lookup of an event hub's slug in a locale: whether `/sale/{slug}` is published there. */
export type ResolveSale = (slug: string, locale: Locale) => Promise<boolean>;

export type SwitchPlan =
  /** Navigate to `path`. */
  | { kind: 'path'; path: string }
  /** Stay on the page; only the remembered country changes. */
  | { kind: 'stay' }
  /** Look the entity up in the other country; `fallback` (its home) when it is not there. */
  | { kind: 'entity'; type: EntityType; key: string; fallback: string }
  /** Look the event hub up in the other country; `fallback` (its calendar) when it does not run there. */
  | { kind: 'sale'; slug: string; path: string; fallback: string };

const ENTITY_TYPES: ReadonlySet<string> = new Set(['brand', 'shop', 'store', 'promotion']);

/** Catalog pages that exist under every launched locale, without their query. */
const SAME_PATH_PAGES: ReadonlySet<string> = new Set(['/brands', '/promotions', '/shops', '/nearby', '/search']);

function decode(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/** The `?query=` of a search URL, as a query string ("" when there is none). */
function searchQuery(search: string): string {
  const value = new URLSearchParams(search).get('query');
  return value ? `?${new URLSearchParams({ query: value }).toString()}` : '';
}

/**
 * What switching to `to` does on `pathname` (+ `search`); a path's own prefix
 * is the country it comes from. `flags`: the backend's `hasSaleEvents` per
 * country (@/seo/saleCountries), absent while unknown.
 */
export function planCountrySwitch(
  pathname: string,
  search: string,
  to: Locale,
  flags?: SaleCountryFlags | null,
): SwitchPlan {
  // The catalog pages at the URLs they are served at (un-prefixed for the
  // default country until the URL move, under the prefix for any other).
  const home = catalogPath(to);
  const split = splitLocalePath(pathname);

  if (!split) {
    if (isAccountPath(pathname)) return { kind: 'stay' };
    // An un-prefixed entity link (`/brand/{id}`) is one entity too.
    const legacy = /^\/(brand|shop|store|promotion)\/([^/]+)\/?$/i.exec(pathname);
    const key = legacy ? decode(legacy[2]) : null;
    if (legacy && key) return { kind: 'entity', type: legacy[1].toLowerCase() as EntityType, key, fallback: home };
    return { kind: 'path', path: home };
  }

  const rest = split.path.replace(/\/+$/, '') || '/';
  const lower = rest.toLowerCase();
  if (lower === '/') return { kind: 'path', path: home };
  if (lower === '/search') return { kind: 'path', path: `${catalogPath(to, '/search')}${searchQuery(search)}` };
  if (SAME_PATH_PAGES.has(lower)) return { kind: 'path', path: catalogPath(to, lower) };

  if (lower === '/sales') {
    return { kind: 'path', path: hasSaleEvents(to, flags) ? localePath(to, '/sales') : home };
  }
  const segments = rest.split('/').filter(Boolean);
  if (segments[0]?.toLowerCase() === 'sale' && segments.length === 2) {
    if (!hasSaleEvents(to, flags)) return { kind: 'path', path: home };
    const slug = segments[1].toLowerCase();
    return { kind: 'sale', slug, path: localePath(to, `/sale/${slug}`), fallback: localePath(to, '/sales') };
  }
  if (segments[0]?.toLowerCase() === 'category' && (segments.length === 2 || segments.length === 3)) {
    return { kind: 'path', path: localePath(to, `/${segments.join('/').toLowerCase()}`) };
  }
  const type = segments[0]?.toLowerCase();
  if (type && ENTITY_TYPES.has(type)) {
    // `/brand/{brand}/{city}` is the brand × city hub: its brand is what carries over.
    if (segments.length === 2 || (type === 'brand' && segments.length === 3)) {
      const key = decode(segments[1]);
      if (key) return { kind: 'entity', type: type as EntityType, key, fallback: home };
    }
  }
  return { kind: 'path', path: home };
}

/**
 * The URL to go to for a plan: the plan's own path, or for an entity its path
 * in `to` (a 200 or 301 answer whose `canonicalPath` is under `to`'s prefix,
 * or un-prefixed for DEFAULT_LOCALE: its id and pre-move URLs), else
 * `fallback`. A failed or malformed lookup falls back too; the selector
 * never strands the visitor on a 404.
 */
export async function resolveSwitchTarget(
  plan: SwitchPlan,
  to: Locale,
  resolve: ResolveEntity,
  resolveSale?: ResolveSale,
): Promise<string | null> {
  if (plan.kind === 'stay') return null;
  if (plan.kind === 'path') return plan.path;
  if (plan.kind === 'sale') {
    try {
      // Without a lookup the event is not known to be there: the calendar always is.
      if (resolveSale && (await resolveSale(plan.slug, to))) return plan.path;
    } catch {
      // fall through
    }
    return plan.fallback;
  }
  try {
    const answer = await resolve(plan.type, plan.key, to);
    const path = answer?.canonicalPath ?? null;
    if (
      answer &&
      (answer.status === 200 || answer.status === 301) &&
      path &&
      /^\/(?![/\\])/.test(path) &&
      (splitLocalePath(path)?.locale ?? DEFAULT_LOCALE) === to
    ) {
      return path;
    }
  } catch {
    // fall through
  }
  return plan.fallback;
}
